import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    User,
    Mail,
    Phone,
    Globe,
    MapPin,
    GraduationCap,
    Users,
    CheckCircle2,
    Loader2,
} from "lucide-react";
import { submitSmruLead } from "../../lib/api";

const FILLING_OPTIONS = ["Student", "Parent", "Guardian"];
const EDUCATION_OPTIONS = [
    "Completed 12th/Intermediate 2nd Year",
    "Studying 12th/Intermediate 2nd Year",
    "Studying 11th/Intermediate 1st Year",
    "Completed 10th Class",
    "Studying 10th Class",
    "Degree",
    "Diploma",
];

const fieldWrap = "mb-5";
const labelCls = "block text-sm font-semibold text-gray-800 mb-2";
const inputCls =
    "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:border-aiblue focus:ring-2 focus:ring-aiblue/20 outline-none transition";
const inputWithIcon =
    "w-full rounded-xl border border-gray-200 bg-white pl-11 pr-4 py-3 text-gray-900 placeholder:text-gray-400 focus:border-aiblue focus:ring-2 focus:ring-aiblue/20 outline-none transition";

/**
 * Shared SMRU /RFC lead form. No OTP. Posts to the `smru_students` collection.
 *
 * variant: "guidance" | "brochure"
 *  - guidance  → "Connect with our best team", shows a thank-you state
 *  - brochure  → "Get the Brochure", downloads the PDF on success
 */
export default function SmruLeadForm({
    variant = "guidance",
    eyebrow,
    title,
    subtitle,
    submitLabel,
    brochureUrl = "/general/ai.prof_brochure.pdf",
}) {
    const [form, setForm] = useState({
        filling_for: "",
        full_name: "",
        mobile_number: "",
        email: "",
        preferred_language: "",
        state: "",
        current_education: "",
    });
    const [agreed, setAgreed] = useState(true);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [already, setAlready] = useState(false);

    const set = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.full_name || form.full_name.trim().length < 2)
            e.full_name = "Please enter your name.";
        if (!/^\d{10}$/.test(form.mobile_number.replace(/\D/g, "").slice(-10)))
            e.mobile_number = "Enter a valid 10-digit mobile number.";
        // Brochure flow doesn't collect an email.
        if (variant !== "brochure" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
            e.email = "Enter a valid email address.";
        if (!agreed) e.agreed = "Please accept the terms to continue.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const downloadBrochure = () => {
        const a = document.createElement("a");
        a.href = brochureUrl;
        a.download = "AI.Prof-Brochure.pdf";
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            await submitSmruLead({
                full_name: form.full_name.trim(),
                mobile_number: form.mobile_number.replace(/\D/g, "").slice(-10),
                email: form.email.trim().toLowerCase() || null,
                filling_for: form.filling_for || null,
                preferred_language: form.preferred_language || null,
                state: form.state.trim() || null,
                current_education: form.current_education || null,
                agreed_terms: agreed,
                lead_type: variant,
                source: "RFC",
            });
            setDone(true);
            if (variant === "brochure") downloadBrochure();
        } catch (err) {
            // Already submitted with this phone/email → treat as a friendly success.
            if (err?.alreadyRegistered || err?.status === 409) {
                setAlready(true);
                setDone(true);
                if (variant === "brochure") downloadBrochure();
            } else {
                setErrors({ form: err?.message || "Something went wrong. Please try again." });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] via-white to-aisky/20 px-4 py-10 sm:py-14">
            {/* decorative blobs */}
            <div className="pointer-events-none absolute top-10 right-0 w-[420px] h-[420px] rounded-full blur-3xl opacity-40 bg-gradient-to-tr from-aiorange to-aipeach float-fast" />

            <div className="relative max-w-xl mx-auto">
                {/* brand logo lockup */}
                <div className="flex items-center justify-center gap-5 mb-6 pt-2">
                    <img
                        src="/ai_prof.png"
                        alt="AI.Prof"
                        className="h-12 sm:h-14 w-auto object-contain"
                    />
                    <span className="text-aiorange font-black text-3xl leading-none">×</span>
                    <img
                        src="/partners/smru/smrulogo.png"
                        alt="St. Mary's University (SMRU)"
                        className="h-14 sm:h-16 w-auto object-contain"
                    />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-[2rem] shadow-xl border border-gray-100 p-7 sm:p-10"
                >
                    {done ? (
                        <div className="text-center py-8">
                            <div className="mx-auto mb-5 h-16 w-16 rounded-full bg-aiblue/10 flex items-center justify-center">
                                <CheckCircle2 className="h-9 w-9 text-aiblue" />
                            </div>
                            <h2 className="font-display text-2xl font-black text-gray-900 mb-2">
                                {already
                                    ? "You're already on the list 🙌"
                                    : variant === "brochure"
                                    ? "Your brochure is downloading 🎉"
                                    : "You're all set 🎉"}
                            </h2>
                            <p className="text-gray-600 mb-6">
                                {already
                                    ? "You've already requested assistance — our SMRU admissions team will reach out to you shortly."
                                    : variant === "brochure"
                                    ? "If the download didn't start, tap the button below. Our team will also reach out shortly."
                                    : "Thank you! Our SMRU admissions team will connect with you very soon."}
                            </p>
                            {variant === "brochure" && (
                                <button
                                    onClick={downloadBrochure}
                                    className="inline-flex items-center gap-2 bg-aiblue hover:bg-blue-700 text-white rounded-full px-7 py-3 font-semibold transition mb-3"
                                >
                                    Download again
                                </button>
                            )}
                            <div>
                                <Link
                                    to="/RFC"
                                    className="inline-block text-sm font-semibold text-aiblue hover:text-aiorange transition mt-2"
                                >
                                    ← Back to the page
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="text-center mb-7">
                                {eyebrow && (
                                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-aiorange/10 text-aiorange font-bold text-xs uppercase tracking-wider mb-4">
                                        {eyebrow}
                                    </span>
                                )}
                                <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-gray-900 leading-tight">
                                    {title}
                                </h1>
                                {subtitle && (
                                    <p className="mt-3 text-gray-600">{subtitle}</p>
                                )}
                            </div>

                            <form onSubmit={onSubmit} noValidate>
                                {/* Who is filling this */}
                                <div className={fieldWrap}>
                                    <label className={labelCls}>Who is filling this?</label>
                                    <div className="relative">
                                        <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <select
                                            value={form.filling_for}
                                            onChange={set("filling_for")}
                                            className={inputWithIcon + " appearance-none"}
                                        >
                                            <option value="">--Select--</option>
                                            {FILLING_OPTIONS.map((o) => (
                                                <option key={o} value={o}>{o}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Name */}
                                <div className={fieldWrap}>
                                    <label className={labelCls}>Your Name *</label>
                                    <div className="relative">
                                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            type="text"
                                            value={form.full_name}
                                            onChange={set("full_name")}
                                            placeholder="Enter your Name"
                                            className={inputWithIcon}
                                        />
                                    </div>
                                    {errors.full_name && (
                                        <p className="mt-1.5 text-sm text-red-500">{errors.full_name}</p>
                                    )}
                                </div>

                                {/* Mobile (no OTP) */}
                                <div className={fieldWrap}>
                                    <label className={labelCls}>Your Mobile Number *</label>
                                    <div className="flex items-stretch rounded-xl border border-gray-200 bg-white focus-within:border-aiblue focus-within:ring-2 focus-within:ring-aiblue/20 overflow-hidden">
                                        <span className="flex items-center gap-1 px-3 text-gray-700 bg-gray-50 border-r border-gray-200 text-sm font-medium">
                                            <Phone className="h-4 w-4 text-gray-400" /> +91
                                        </span>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            value={form.mobile_number}
                                            onChange={set("mobile_number")}
                                            placeholder="Enter 10 Digit Mobile Number"
                                            className="flex-1 px-4 py-3 text-gray-900 placeholder:text-gray-400 outline-none"
                                        />
                                    </div>
                                    {errors.mobile_number && (
                                        <p className="mt-1.5 text-sm text-red-500">{errors.mobile_number}</p>
                                    )}
                                </div>

                                {/* Email — not collected for the brochure flow */}
                                {variant !== "brochure" && (
                                    <div className={fieldWrap}>
                                        <label className={labelCls}>Email *</label>
                                        <div className="relative">
                                            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                type="email"
                                                value={form.email}
                                                onChange={set("email")}
                                                placeholder="Enter your email address"
                                                className={inputWithIcon}
                                            />
                                        </div>
                                        {errors.email && (
                                            <p className="mt-1.5 text-sm text-red-500">{errors.email}</p>
                                        )}
                                    </div>
                                )}

                                {/* Preferred Language */}
                                <div className={fieldWrap}>
                                    <label className={labelCls}>Preferred Language</label>
                                    <div className="relative">
                                        <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            type="text"
                                            value={form.preferred_language}
                                            onChange={set("preferred_language")}
                                            placeholder="Enter your preferred language"
                                            className={inputWithIcon}
                                        />
                                    </div>
                                </div>

                                {/* State */}
                                <div className={fieldWrap}>
                                    <label className={labelCls}>State</label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            type="text"
                                            value={form.state}
                                            onChange={set("state")}
                                            placeholder="Enter your State"
                                            className={inputWithIcon}
                                        />
                                    </div>
                                </div>

                                {/* Current Education */}
                                <div className={fieldWrap}>
                                    <label className={labelCls}>Current Education</label>
                                    <div className="relative">
                                        <GraduationCap className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <select
                                            value={form.current_education}
                                            onChange={set("current_education")}
                                            className={inputWithIcon + " appearance-none"}
                                        >
                                            <option value="">--Select--</option>
                                            {EDUCATION_OPTIONS.map((o) => (
                                                <option key={o} value={o}>{o}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Terms */}
                                <label className="flex items-start gap-3 mt-2 mb-6 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={agreed}
                                        onChange={(e) => {
                                            setAgreed(e.target.checked);
                                            setErrors((er) => ({ ...er, agreed: undefined }));
                                        }}
                                        className="mt-1 h-5 w-5 rounded accent-aiblue"
                                    />
                                    <span className="text-sm text-gray-600">
                                        By proceeding further, I agree to the{" "}
                                        <a
                                            href="https://aiprof.com/legal/terms"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-aiblue font-semibold hover:underline"
                                        >
                                            Terms &amp; Conditions
                                        </a>{" "}
                                        and{" "}
                                        <a
                                            href="https://aiprof.com/legal/privacy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-aiblue font-semibold hover:underline"
                                        >
                                            Privacy Policy
                                        </a>{" "}
                                        of AI.Prof
                                    </span>
                                </label>
                                {errors.agreed && (
                                    <p className="-mt-4 mb-4 text-sm text-red-500">{errors.agreed}</p>
                                )}

                                {errors.form && (
                                    <p className="mb-4 text-sm text-red-500 text-center">{errors.form}</p>
                                )}

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full inline-flex items-center justify-center gap-2 bg-aiblue hover:bg-blue-700 disabled:opacity-60 text-white rounded-full px-7 py-4 text-base font-semibold transition cta-pulse"
                                >
                                    {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
                                    {submitting ? "Submitting…" : submitLabel}
                                </button>
                            </form>
                        </>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
